import { useEffect, useMemo, useRef, useState, type ChangeEvent } from "react";
import { Inbox } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";

import { humanizeApiError } from "../api/client";
import {
  DOCUMENT_STATUS_LABELS,
  type Document,
  type DocumentStatus,
  documentsApi,
  humanizeStatusError,
} from "../api/documents";
import { useAuth } from "../auth/useAuth";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { ConfirmDialog } from "../components/ui/confirm-dialog";
import { Input } from "../components/ui/input";
import { Pagination } from "../components/ui/pagination";
import { Select } from "../components/ui/select";
import { TableSkeleton } from "../components/ui/table-skeleton";

const PAGE_SIZE = 20;
const ACCEPTED_FORMATS = ".pdf,.pptx";
const MAX_UPLOAD_MB = 50; // см. backend services/documents.py:MAX_UPLOAD_BYTES (PRD §5.6)
const SEARCH_DEBOUNCE_MS = 300;

const STATUS_OPTIONS: { value: DocumentStatus | ""; label: string }[] = [
  { value: "", label: "Все статусы" },
  { value: "ready", label: "Готово" },
  { value: "processing", label: "Обрабатывается" },
  { value: "conversion_error", label: "Ошибка конвертации" },
];

function statusToneClass(status: DocumentStatus): string {
  switch (status) {
    case "ready":
      return "text-emerald-700 bg-emerald-50";
    case "processing":
    case "uploading":
      return "text-amber-700 bg-amber-50";
    case "conversion_error":
      return "text-red-700 bg-red-50";
  }
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} КБ`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString("ru-RU", { dateStyle: "short", timeStyle: "short" });
}

function extractMessage(err: unknown): string {
  return humanizeApiError(err, { entityLabel: "Документ" });
}

export function DocumentsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [items, setItems] = useState<Document[]>([]);
  const [total, setTotal] = useState(0);

  // Помечаем документы, чьё нормализованное название встречается на
  // текущей странице больше одного раза. Это типичный кейс «менеджер
  // перезаливает PDF, не удалив старый» — а dispatches ссылаются на
  // конкретный document_id, так что прошлая версия остаётся жить.
  // Видеть это в списке полезно, чтобы решить, какую запись чистить.
  const duplicateTitles = useMemo(() => {
    const counts = new Map<string, number>();
    for (const d of items) {
      const key = d.title.trim().toLowerCase();
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return new Set(
      [...counts.entries()]
        .filter(([, n]) => n > 1)
        .map(([k]) => k),
    );
  }, [items]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [deleteTarget, setDeleteTarget] = useState<Document | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();
  // ?needs_review=1 ставится из ManualReviewAlert (PRD §17 №19). Используем
  // URL как источник истины — кнопка-переключатель на странице тоже
  // переписывает параметр.
  const needsReview = searchParams.get("needs_review") === "1";

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<DocumentStatus | "">("");

  useEffect(() => {
    const t = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [search]);

  const filter = useMemo(
    () => ({
      needs_review: needsReview ? true : undefined,
      status: statusFilter || undefined,
      search: debouncedSearch || undefined,
    }),
    [needsReview, statusFilter, debouncedSearch],
  );

  // Сбрасываем страницу на 1 при любом изменении фильтров.
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setPage(1);
  }, [needsReview, statusFilter, debouncedSearch]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    documentsApi
      .list({ page, size: PAGE_SIZE, ...filter })
      .then((r) => {
        if (cancelled) return;
        setItems(r.items);
        setTotal(r.total_count);
      })
      .catch((err) => {
        if (!cancelled) setError(extractMessage(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [page, filter]);

  // Если в списке есть документы в обработке — лёгкий поллинг каждые 5с.
  useEffect(() => {
    const hasPending = items.some(
      (d) => d.status === "processing" || d.status === "uploading",
    );
    if (!hasPending) return;
    const t = window.setTimeout(() => {
      documentsApi
        .list({ page, size: PAGE_SIZE, ...filter })
        .then((r) => {
          setItems(r.items);
          setTotal(r.total_count);
        })
        .catch(() => {
          // Тихий поллинг — ошибки не показываем повторно.
        });
    }, 5_000);
    return () => window.clearTimeout(t);
  }, [items, page, filter]);

  async function onConfirmDelete() {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await documentsApi.delete(deleteTarget.id);
      setDeleteTarget(null);
      // Если на странице остался один элемент и есть предыдущая —
      // сдвинемся назад, иначе таблица покажет пустоту до перезагрузки.
      const nextPage = items.length === 1 && page > 1 ? page - 1 : page;
      const r = await documentsApi.list({
        page: nextPage,
        size: PAGE_SIZE,
        ...filter,
      });
      setItems(r.items);
      setTotal(r.total_count);
      if (nextPage !== page) setPage(nextPage);
    } catch (err) {
      // 409 — есть активные рассылки; бэк отдаёт человеческий message.
      setDeleteError(extractMessage(err));
    } finally {
      setDeleteBusy(false);
    }
  }

  async function onFileChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      await documentsApi.upload({ file });
      setPage(1);
      const r = await documentsApi.list({
        page: 1,
        size: PAGE_SIZE,
        ...filter,
      });
      setItems(r.items);
      setTotal(r.total_count);
    } catch (err) {
      setError(extractMessage(err));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const hasFilters =
    needsReview || statusFilter !== "" || debouncedSearch.length > 0;

  function clearFilters() {
    setSearch("");
    setStatusFilter("");
    setSearchParams({});
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Документы</h1>
        <div className="flex flex-col items-end gap-1">
          <Input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_FORMATS}
            onChange={onFileChange}
            disabled={uploading}
            className="hidden"
          />
          <Button
            disabled={uploading}
            onClick={() => fileInputRef.current?.click()}
          >
            {uploading ? "Загрузка…" : "Загрузить документ"}
          </Button>
          <span className="text-xs text-muted-foreground">
            PDF или PPTX, до {MAX_UPLOAD_MB} МБ
          </span>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Input
          type="search"
          placeholder="Поиск по названию документа"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-xs"
          aria-label="Поиск по названию документа"
        />
        <Select
          value={statusFilter}
          onChange={(e) =>
            setStatusFilter(e.target.value as DocumentStatus | "")
          }
          className="w-auto"
          aria-label="Фильтр по статусу"
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Select>
        <div
          role="tablist"
          aria-label="Фильтр требующих проверки"
          className="inline-flex rounded-md border border-input bg-background p-0.5 text-sm"
        >
          <button
            type="button"
            role="tab"
            aria-selected={!needsReview}
            onClick={() => {
              const next = new URLSearchParams(searchParams);
              next.delete("needs_review");
              setSearchParams(next);
            }}
            className={
              "rounded px-3 py-1 transition-colors " +
              (!needsReview
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground")
            }
          >
            Все
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={needsReview}
            onClick={() => {
              const next = new URLSearchParams(searchParams);
              next.set("needs_review", "1");
              setSearchParams(next);
            }}
            data-testid="needs-review-filter-toggle"
            className={
              "rounded px-3 py-1 transition-colors " +
              (needsReview
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground")
            }
          >
            Требуют проверки
          </button>
        </div>
        {hasFilters && (
          <button
            type="button"
            onClick={clearFilters}
            className="text-sm text-muted-foreground hover:text-foreground underline"
          >
            Сбросить фильтры
          </button>
        )}
      </div>

      {error && (
        <Card>
          <CardContent className="text-red-700 text-sm py-3">{error}</CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base font-medium">
            {needsReview ? "Требуют проверки" : "Всего документов"}: {total}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-muted-foreground">
              <tr className="border-b">
                <th className="text-left font-normal px-4 py-2">Название</th>
                <th className="text-left font-normal px-4 py-2">Размер</th>
                <th className="text-left font-normal px-4 py-2">Страниц</th>
                <th className="text-left font-normal px-4 py-2">Статус</th>
                <th className="text-left font-normal px-4 py-2">Кем загружен</th>
                <th className="text-left font-normal px-4 py-2">Дата</th>
                {isAdmin && (
                  <th className="text-right font-normal px-4 py-2 w-px"></th>
                )}
              </tr>
            </thead>
            <tbody>
              {loading && items.length === 0 ? (
                <TableSkeleton columns={isAdmin ? 7 : 6} />
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={isAdmin ? 7 : 6} className="px-4 py-8">
                    {hasFilters ? (
                      <div className="inline-flex w-full items-center justify-center gap-3 font-mono text-sm text-muted-foreground">
                        <Inbox className="h-3.5 w-3.5" aria-hidden="true" />
                        Ничего не нашли по вашему запросу
                        <button
                          type="button"
                          onClick={clearFilters}
                          className="text-primary underline hover:no-underline"
                        >
                          сбросить фильтры
                        </button>
                      </div>
                    ) : (
                      <div className="mx-auto max-w-sm neu-canvas rounded-2xl px-6 py-7 flex flex-col items-center gap-3 text-center">
                        <div className="neu-inset rounded-full p-3 text-muted-foreground">
                          <Inbox className="h-5 w-5" aria-hidden="true" />
                        </div>
                        <p className="font-medium text-foreground">
                          Здесь пока нет документов
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Загрузите первый файл — он появится в этой таблице.
                        </p>
                        <Button
                          disabled={uploading}
                          onClick={() => fileInputRef.current?.click()}
                          className="mt-1"
                        >
                          Загрузить первый
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ) : (
                items.map((doc) => (
                  <tr key={doc.id} className="border-b last:border-b-0">
                    <td className="px-4 py-2 font-medium max-w-md">
                      <div className="flex items-center gap-2 min-w-0">
                        <span
                          className="shrink-0 inline-block rounded bg-muted px-1.5 py-0.5 text-[10px] font-mono uppercase text-muted-foreground"
                          title={`Формат: ${doc.original_format.toUpperCase()}`}
                        >
                          {doc.original_format}
                        </span>
                        <Link
                          to={`/documents/${doc.id}`}
                          className="text-blue-700 hover:underline truncate min-w-0"
                          title={doc.title}
                        >
                          {doc.title}
                        </Link>
                        {duplicateTitles.has(doc.title.trim().toLowerCase()) && (
                          <span
                            data-testid={`duplicate-badge-${doc.id}`}
                            title="На этой странице есть ещё документ(ы) с таким же названием. Возможно, это разные версии одного файла."
                            className="shrink-0 inline-block rounded px-1.5 py-0.5 text-[10px] uppercase tracking-wider bg-amber-50 text-amber-800"
                          >
                            дубль
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2">{formatSize(doc.original_size_bytes)}</td>
                    <td className="px-4 py-2">{doc.pages_count ?? "—"}</td>
                    <td className="px-4 py-2">
                      <span
                        className={`inline-block rounded px-2 py-0.5 text-xs ${statusToneClass(doc.status)}`}
                      >
                        {DOCUMENT_STATUS_LABELS[doc.status]}
                      </span>
                      {doc.needs_manual_review && (
                        <span
                          data-testid="needs-review-badge"
                          title="После конвертации число страниц не совпало с оригиналом. Откройте документ и сверьте со страницами оригинала — при необходимости перезалейте файл."
                          className="ml-1 inline-block rounded px-2 py-0.5 text-xs text-amber-800 bg-amber-100"
                        >
                          Проверьте страницы
                        </span>
                      )}
                      {doc.status === "conversion_error" && doc.status_error && (
                        <div
                          className="text-xs text-red-600 mt-1 max-w-xs"
                          title={doc.status_error}
                        >
                          {humanizeStatusError(doc.status_error)}
                        </div>
                      )}
                    </td>
                    <td
                      className="px-4 py-2 text-muted-foreground"
                      title={doc.owner_email ?? undefined}
                    >
                      {doc.owner_full_name ?? doc.owner_email ?? "—"}
                    </td>
                    <td className="px-4 py-2 text-muted-foreground">
                      {formatDate(doc.created_at)}
                    </td>
                    {isAdmin && (
                      <td className="px-4 py-2 text-right">
                        <button
                          type="button"
                          onClick={() => {
                            setDeleteError(null);
                            setDeleteTarget(doc);
                          }}
                          className="text-xs text-muted-foreground hover:text-red-700 underline-offset-2 hover:underline"
                          aria-label={`Удалить документ «${doc.title}»`}
                        >
                          Удалить
                        </button>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Pagination
        page={page}
        totalPages={totalPages}
        total={total}
        disabled={loading}
        onPageChange={(next) => setPage(next)}
      />

      <ConfirmDialog
        open={deleteTarget !== null}
        title={
          deleteTarget
            ? `Удалить документ «${deleteTarget.title}»?`
            : "Удалить документ?"
        }
        description={
          <div className="space-y-2">
            <p>
              Документ и все его страницы будут удалены без возможности
              восстановления. Активные рассылки удалить нельзя — сначала
              отключите их.
            </p>
            {deleteError && (
              <p className="text-red-700" data-testid="delete-error">
                {deleteError}
              </p>
            )}
          </div>
        }
        confirmLabel="Удалить"
        variant="destructive"
        busy={deleteBusy}
        onConfirm={onConfirmDelete}
        onCancel={() => {
          if (deleteBusy) return;
          setDeleteTarget(null);
          setDeleteError(null);
        }}
      />
    </div>
  );
}
